import { notification } from 'antd';
import axios, { AxiosResponse } from 'axios';
import { showPageMask } from 'src/shared/page-mask';
import { sleepForever } from 'src/utils';

import { AlreadyLockedException, OutOfDateException } from '../common/SeahorseError';
import SeahorseJson, { isSeahorseErrorJson, SeahorseErrorJson } from '../json/seahorse-json';
import { setExtensionTaxonomyGuid, setPageVersion } from '../tagging/xhtml.tagging';

export const MaxModelBindingCollectionSize = 1024;

axios.interceptors.response.use(
  async (response: AxiosResponse<SeahorseJson<unknown>>) => {
    const { data } = response;
    if (!isSeahorseErrorJson(data)) {
      setExtensionTaxonomyGuid(data.ExtensionTaxonomyGuid);
      setPageVersion(data.PageVersion);
      if (data.ContainerHtml && data.ContainerId) {
        window.injectDocumentContent(data.ContainerId, data.ContainerHtml);
      }
    }
    return response;
  },
  (error) => {
    if (error.response?.status) {
      return handleStatus(error.response);
    }
    return Promise.reject(error);
  },
);

axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

export function handleStatus<T extends { status: number }>(response: T, allow404 = false): Promise<T> {
  if ((response.status >= 200 && response.status < 400) || (response.status === 404 && allow404)) {
    return Promise.resolve(response);
  }
  switch (response.status) {
    case 401:
      showPageMask(UiText.t('Shared_LoggingOut_Text'));
      location.reload();
      return sleepForever();
    case 413:
      throw Error(UiText.t('DocumentUpload_FileTooBigGeneric_Error'));
    default:
      throw Error(UiText.t('Shared_UnknownErrorOccured_Error'));
  }
}

export function getResponse<T>(response: SeahorseJson<T>): T {
  if (!response.Success) {
    switch (response.ErrorCode) {
      case 'AlreadyLockedException':
        throw new AlreadyLockedException(response.Message);
      case 'OutOfDateException':
        throw new OutOfDateException(response.Message);
      default:
        throw new Error(response.Message);
    }
  }
  setPageVersion(response.PageVersion);
  setExtensionTaxonomyGuid(response.ExtensionTaxonomyGuid);
  return response.Result;
}

export function getResponseOrShowError<T>(response: AxiosResponse<SeahorseJson<T>>, errorMessage?: string): T {
  try {
    return getResponse<T>(response.data);
  } catch (err) {
    console.error(err);
    notification.error({ message: errorMessage ?? (response.data as SeahorseErrorJson).Message });
    throw err;
  }
}

export default abstract class ShApi {
  protected checkResponse<T>(response: SeahorseJson<T>): T {
    return getResponse(response);
  }

  protected checkResponseOrShowError<T>(response: AxiosResponse<SeahorseJson<T>>): T {
    return getResponseOrShowError(response);
  }
}
